import { createStitches } from '@stitches/react';
import type { CSS, ScaleValue, PropertyValue } from '@stitches/react';

export const {
  styled,
  css,
  theme,
  createTheme,
  getCssText,
  globalCss,
  keyframes,
  config,
  reset,
} = createStitches({
  prefix: 'backyard-ui',
  theme: {
    borderWidths: {
      xs: '1px',
      sm: '2px',
      md: '3px',
      lg: '4px',
      xl: '5px',
    },
    radii: {
      xs: '2px',
      sm: '4px',
      md: '8px',
      lg: '12px',
      xl: '16px',
      full: '100%',
    },
    space: {
      '0': '0px',
      '1': '4px',
      '2': '8px',
      '3': '12px',
      '4': '16px',
      '5': '20px',
      '6': '24px',
      '7': '28px',
      '8': '32px',
      '9': '36px',
      '10': '40px',
      '11': '44px',
      '12': '48px',
      '14': '56px',
      '16': '64px',
      '20': '80px',
      '24': '96px',
      '28': '112px',
      '32': '128px',
      '36': '144px',
      '40': '160px',
      '44': '176px',
      '48': '192px',
      '52': '208px',
      '56': '224px',
      '60': '240px',
      '64': '256px',
      '72': '288px',
      '80': '320px',
      '96': '384px',
      full: '100%',
    },
    sizes: {
      '0': '0px',
      '1': '4px',
      '2': '8px',
      '3': '12px',
      '4': '16px',
      '5': '20px',
      '6': '24px',
      '7': '28px',
      '8': '32px',
      '9': '36px',
      '10': '40px',
      '11': '44px',
      '12': '48px',
      '14': '56px',
      '16': '64px',
      '20': '80px',
      '24': '96px',
      '28': '112px',
      '32': '128px',
      '36': '144px',
      '40': '160px',
      '44': '176px',
      '48': '192px',
      '52': '208px',
      '56': '224px',
      '60': '240px',
      '64': '256px',
      '72': '288px',
      '80': '320px',
      '96': '384px',
      full: '100%',
    },
    fonts: {
      default: 'Leroy Merlin Sans, sans-serif',
      mono: 'Menlo, monospace',
    },
    fontSizes: {
      //text
      txs: '10px',
      tsm: '12px',
      tmd: '13px',
      tlg: '16px',
      txl: '18px',
      t2xl: '22px',
      t3xl: '24px',
      t4xl: '28px',
      t5xl: '36px',
      t6xl: '42px',

      //heading
      hxs: '10px',
      hsm: '12px',
      hmd: '13px',
      hlg: '16px',
      hxl: '18px',
      h2xl: '22px',
      h3xl: '24px',
      h4xl: '28px',
      h5xl: '36px',
      h6xl: '42px',
    },
    fontWeights: {
      hairline: 100,
      thin: 200,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
    },
    colors: {
      // static
      white: '#FFFFFF',
      black: '#000000',

      // critical
      c100: '#FEEDEE',
      c200: '#FAB9BC',
      c300: '#F38789',
      c400: '#EA5D58',
      c500: '#DF382B',
      c600: '#B42A27',
      c700: '#8B2226',
      c800: '#641B21',
      c900: '#3E1219',

      // info
      i100: '#ECFCFD',
      i200: '#B7EEF5',
      i300: '#84DBEB',
      i400: '#54C4DE',
      i500: '#25A8D0',
      i600: '#2282A8',
      i700: '#1E5F82',
      i800: '#18415D',
      i900: '#102739',

      // neutral
      n100: '#EEEEF0',
      n200: '#D3D2D6',
      n300: '#BAB6BC',
      n400: '#A19BA2',
      n500: '#887F87',
      n600: '#6F676C',
      n700: '#554F52',
      n800: '#3C3738',
      n900: '#222020',

      // orange
      o100: '#FFF5EB',
      o200: '#FFDAB2',
      o300: '#FFC170',
      o400: '#FFAB48',
      o500: '#FC961E',
      o600: '#C86F21',
      o700: '#974F20',
      o800: '#6A351C',
      o900: '#401F15',

      // primary
      p100: '#EAF3E2',
      p200: '#CBE3B5',
      p300: '#ADD585',
      p400: '#91C854',
      p500: '#78BE20',
      p600: '#41A017',
      p700: '#158110',
      p800: '#0A601B',
      p900: '#053E1F',

      // secondary
      s100: '#DBEDEA',
      s200: '#A5D1CB',
      s300: '#6EB4AD',
      s400: '#379590',
      s500: '#007574',
      s600: '#035F64',
      s700: '#044C53',
      s800: '#063A44',
      s900: '#062B35',

      // tertiary
      t100: '#E9D0FF',
      t200: '#C48FF3',
      t300: '#AD63F0',
      t400: '#9040E6',
      t500: '#7808CF',
      t600: '#6C0CAE',
      t700: '#570887',
      t800: '#480870',
      t900: '#350C52',

      // violet
      v100: '#FBE9FF',
      v200: '#FADCFF',
      v300: '#F6BDFF',
      v400: '#D078DA',
      v500: '#B92ECB',
      v600: '#9A21AA',
      v700: '#791186',
      v800: '#4E1155',
      v900: '#350D3A',

      // warning
      w100: '#FFF8E2',
      w200: '#FFEBAF',
      w300: '#FEE07D',
      w400: '#FCD64C',
      w500: '#F8CC1C',
      w600: '#C9A21B',
      w700: '#9C7B18',
      w800: '#705613',
      w900: '#46350D',

      // misc
      shadow: 'rgba(60, 55, 56, 0.07)',
    },
    shadows: {
      xs: '0 0 0 1px $colors$shadow, 0 0 1px 0 $colors$shadow, 0 2px 2px 0 $colors$shadow',
      sm: '0 0 0 1px $colors$shadow, 0 2px 2px 0 $colors$shadow, 0 4px 4px 0 $colors$shadow',
      md: '0 0 0 1px $colors$shadow, 0 4px 4px 0 $colors$shadow, 0 8px 8px 0 $colors$shadow',
      lg: '0 0 0 1px $colors$shadow, 0 6px 6px 0 $colors$shadow, 0 8px 8px 0 $colors$shadow',
      xl: '0 0 0 1px $colors$shadow, 0 8px 8px 0 $colors$shadow, 0 12px 12px 0 $colors$shadow',
    },
  },
  media: {
    xs: '(min-width: 22.5em)',
    sm: '(min-width: 36em)',
    md: '(min-width: 48em)',
    lg: '(min-width: 62em)',
    xl: '(min-width: 75em)',
  },
  utils: {
    marginX: (value: ScaleValue<'space'> | PropertyValue<'margin'>) => ({
      marginLeft: value,
      marginRight: value,
    }),
    marginY: (value: ScaleValue<'space'> | PropertyValue<'margin'>) => ({
      marginTop: value,
      marginBottom: value,
    }),
    paddingX: (value: ScaleValue<'space'> | PropertyValue<'padding'>) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    paddingY: (value: ScaleValue<'space'> | PropertyValue<'padding'>) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
    focus: (value: PropertyValue<'color'>) => ({
      boxShadow: `$colors${value} 0 0 0 2px`,
    }),
  },
});

export type StitchesCSS = CSS<typeof config>;
export type {
  VariantProps,
  CSS,
  CSSProperties,
  PropertyValue,
  ScaleValue,
} from '@stitches/react';
